import { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiAvatar, MiIcon } from "@miplatform/components";
import {
  ATTEMPT_DERIVED_STATUS,
  ATTEMPT_DERIVED_STATUS_COLORS_MAP,
  ATTEMPT_STATUSES,
  COLUMN_WIDTH,
  DEFAULT_COMMUNITY_COLORS,
  HEIGHT_OF_ONE_HOUR,
  MILLISECONDS_PER_SECOND,
  SUBMIT_STATUSES,
} from "@miplatform/constants";
import {
  formatDate,
  getContrastColor,
  getDurationInHours,
  isEmpty,
} from "@miplatform/utils";
import {
  mdiAccountMultiple,
  mdiArrowExpand,
  mdiArrowURightTop,
  mdiCancel,
  mdiCheck,
  mdiCurrencyUsd,
  mdiExclamation,
  mdiHandFrontLeft,
  mdiLockOutline,
  mdiMagnify,
} from "@mdi/js";
import { EmailActivityPreviewModal } from "@mibuildweb/components";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { CLOSEOUT_TAB } from "../CloseoutDrawer/constants";
import { TaskOverview } from "../TaskOverview/TaskOverview";
import { TaskCardMenu } from "./TaskCardMenu/TaskCardMenu";

const SECTION_HEIGHT = HEIGHT_OF_ONE_HOUR * 0.25;
const MAX_ICONS = 4;

export function TaskCard({
  event: attempt = {},
  isSelected = false,
  isMinimized,
  isInBucket: fromBucket = false,
  isNHC = false,
  setHasModalOpen = () => null,
  handleOpenCloseoutDrawer = () => null,
  onEmailDrawerOpen = () => null,
  onEmailModalOpen = () => null,
  onAttemptUpdated = () => null,
  onCardHover = () => null,
  updateCalendarCounts = () => null,
  loading = false,
  searchedAttemptId,
  calendarSet,
  className,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [timeoutId, setTimeoutId] = useState(null);
  const [autoCloseTimeoutId, setAutoCloseTimeoutId] = useState(null);
  const theme = useTheme();
  const [emailPreviewDetails, setEmailPreviewDetails] = useState(null);

  const {
    delayedTime,
    readOnly,
    home = {},
    assignment,
    issues,
    isOnInactiveInterval,
  } = attempt;

  const isInBucket = fromBucket || !attempt.assignedUserId;
  const isInSearch = searchedAttemptId === attempt.id;

  const isRedo = Number(attempt.attemptNumber?.replace("*", "")) > 1;

  const diffInStartTime = readOnly
    ? getDurationInHours(attempt.scheduledStartDate, attempt.displayStartTime)
    : 0;

  const isDelayed = delayedTime > 0 || diffInStartTime > 0;
  const isEarly = diffInStartTime < 0;
  const isMultiTechnician = assignment?.multiTechnician;
  const isAttemptMisaligned = assignment?.isAttemptMisaligned;
  const communityColor = useMemo(() => {
    const { communityName } = home;
    const colors = DEFAULT_COMMUNITY_COLORS;
    if (communityName) {
      const colorIndex =
        communityName.split("").reduce((a, i) => a + i.charCodeAt(0), 0) %
        colors.length;
      return colors[colorIndex];
    }
    return theme.palette.bluegrey.light;
  }, [home.communityName]);

  const { classes, cx } = useStyles({
    submittedStatus: attempt.submittedStatus,
    isSelected,
    isOnInactiveInterval,
    isMinimized,
    communityColor,
    assignmentTemplateColor: assignment?.order?.orderTemplateColor,
    derivedStatus: attempt.derivedStatus,
    isUnscheduled: !!attempt.isUnscheduled,
    hideFooter: !isRedo && isEmpty(issues),
    readOnly,
    isMultiTechnician,
    isAttemptMisaligned,
    loading,
    isInSearch,
    capacityColor: attempt.capacityColor,
    isRequestedInDateRange: attempt.isRequestedInDateRange,
    isInBucket,
  });

  const openPopper = (e) => {
    onCardHover(e);
    const { currentTarget } = e;
    const timeout = setTimeout(() => {
      if (!isEmpty(modalType) || isSelected) {
        return;
      }
      if (!isContextMenuOpen) {
        setAnchorEl(currentTarget);
      }
    }, 500);

    startAutoCloseTimeout();

    setTimeoutId(timeout);
  };

  const closePopper = () => {
    onCardHover();
    setAnchorEl(null);
    clearTimeout(timeoutId);
    stopAutoCloseTimeout();
  };

  const stopAutoCloseTimeout = () => {
    if (autoCloseTimeoutId) {
      clearTimeout(autoCloseTimeoutId);
    }
  };

  const startAutoCloseTimeout = () => {
    const autoCloseTimeout = setTimeout(() => {
      closePopper();
    }, 10 * MILLISECONDS_PER_SECOND);

    setAutoCloseTimeoutId(autoCloseTimeout);
  };

  const openContextMenu = (e) => {
    e.preventDefault();
    setAnchorEl(null);

    setContextMenu(
      !contextMenu && !modalType
        ? { mouseX: e.clientX + 2, mouseY: e.clientY - 6 }
        : null
    );

    setIsContextMenuOpen(true);
  };

  const closeContextMenu = () => {
    setContextMenu(null);
    setIsContextMenuOpen(false);
  };

  const openHomeDetails = () => {
    if (isContextMenuOpen || modalType) {
      return;
    }
    handleOpenCloseoutDrawer(attempt);
  };

  const handleEmailPreviewModalOpen = (details) => {
    closePopper();
    setEmailPreviewDetails(details);
  };

  const handleEmailPreviewModalClose = () => {
    setEmailPreviewDetails(null);
  };

  const footerIcons = [
    {
      show: isRedo,
      path: mdiArrowURightTop,
      color: theme.palette.grey.darkerAccent,
      testId: "footer-icon-redo",
    },
    {
      show: !isEmpty(attempt.lockTypes),
      path: mdiLockOutline,
      color: theme.palette.grey.darkerAccent,
      testId: "footer-icon-lock",
    },
    {
      show: isMultiTechnician,
      path: mdiAccountMultiple,
      color: theme.palette.grey.darkerAccent,
      testId: "footer-icon-multi-technician",
    },
    {
      show: attempt.attemptStatus === ATTEMPT_STATUSES.HOLD,
      path: mdiHandFrontLeft,
      color: theme.palette.purple.main,
      testId: "footer-icon-hold",
    },
    {
      show: isInSearch,
      path: mdiMagnify,
      color: theme.palette.blue.accent,
      testId: "footer-icon-search",
    },
    {
      show:
        [
          ATTEMPT_DERIVED_STATUS.INVOICED,
          ATTEMPT_DERIVED_STATUS.JOB_COMPLETE,
        ].includes(attempt.derivedStatus) ||
        ([
          ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT,
          ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
          ATTEMPT_DERIVED_STATUS.PENDING_INVOICING,
          ATTEMPT_DERIVED_STATUS.CLOSEOUT_CLOSED,
        ].includes(attempt.derivedStatus) &&
          attempt.submittedStatus === SUBMIT_STATUSES.COMPLETE),
      path: mdiCheck,
      size: 0.6,
      color: theme.palette.success.main,
      testId: "footer-icon-check",
    },
    {
      show:
        attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.JOB_INCOMPLETE ||
        ([
          ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT,
          ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
          ATTEMPT_DERIVED_STATUS.PENDING_INVOICING,
          ATTEMPT_DERIVED_STATUS.CLOSEOUT_CLOSED,
        ].includes(attempt.derivedStatus) &&
          attempt.submittedStatus === SUBMIT_STATUSES.INCOMPLETE),
      path: mdiExclamation,
      size: 0.6,
      color: theme.palette.error.main,
      testId: "footer-icon-exclamation",
    },
    {
      show: [
        ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
        ATTEMPT_DERIVED_STATUS.PENDING_INVOICING,
        ATTEMPT_DERIVED_STATUS.INVOICED,
      ].includes(attempt.derivedStatus),
      path: mdiCurrencyUsd,
      color:
        attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.INVOICED
          ? theme.palette.green.main
          : theme.palette.yellow.dark,
      testId: "footer-icon-currency-usd",
    },
    {
      show: attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.CANCELLED,
      path: mdiCancel,
      color: theme.palette.error.main,
      testId: "footer-icon-cancel",
    },
  ].filter(({ show }) => show);

  const cardRef = useRef(null);

  useEffect(() => {
    if (searchedAttemptId && isInSearch && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [isInSearch]);

  if (attempt.isCapacityTracker) {
    return (
      <Box className={classes.capacityCard}>
        <Typography className={classes.capacityText}>
          {attempt.orderTemplateAbbreviation ?? attempt.orderTemplateShortName}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        className={cx(classes.card, className)}
        ref={cardRef}
        onMouseEnter={openPopper}
        onMouseLeave={closePopper}
        data-testid="taskcard-container"
        onContextMenu={openContextMenu}
        onClick={openHomeDetails}
        disabled={loading}
      >
        <Box
          className={classes.header}
          data-testid="community-header"
          onClick={openHomeDetails}
        >
          <Box className={classes.headerContainer}>
            <MiAvatar
              className={classes.attemptType}
              variant="square"
              data-testid="attempt-type"
            >
              {assignment.order?.orderTemplateAbbreviation}
            </MiAvatar>
            <Typography className={classes.community}>
              {home.communityName}
            </Typography>
          </Box>
          <Box className={classes.headerRightContainer}>
            <Typography className={classes.builder}>
              {home.builderShortName}
            </Typography>
            {isMinimized && <MiIcon size={0.4} path={mdiArrowExpand} />}
          </Box>
        </Box>
        <Box className={classes.addressSection}>
          <Typography className={classes.address} noWrap>
            {home.address?.addressLine1}
          </Typography>
          <Divider orientation="vertical" variant="middle" flexItem />
          <Typography className={classes.city}>{home.address?.city}</Typography>
        </Box>
        {isInBucket && attempt.attemptRequestedStartDate && (
          <Box className={classes.requestedStartDateSection}>
            <Typography className={classes.requestedStartDate} noWrap>
              {`Requested: ${formatDate(
                attempt.attemptRequestedStartDate,
                "MM/DD/YYYY"
              )}`}
            </Typography>
          </Box>
        )}
        <Box className={classes.footer}>
          <Typography className={classes.issuesTitle} noWrap>
            {!isEmpty(issues) &&
              issues
                .map((x) => x.title)
                .reduce((acc, v) => `${acc}, ${v}`, "")
                .substring(2)}
          </Typography>
          <Box className={classes.icons}>
            {footerIcons.map(
              (icon, index) =>
                index < MAX_ICONS && (
                  <MiIcon
                    key={`footerIcon-${index}`}
                    size={icon.size ?? 0.5}
                    path={icon.path}
                    color={icon.color}
                    data-testid={icon.testId}
                  />
                )
            )}
            {footerIcons.length > MAX_ICONS && (
              <Typography variant="caption">...</Typography>
            )}
          </Box>
        </Box>
        {attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.INVOICED && (
          <Typography className={classes.invoicedText}>Invoiced</Typography>
        )}
        <TaskOverview
          attempt={attempt}
          issues={issues}
          anchorEl={anchorEl}
          closePopper={closePopper}
          isEarly={isEarly}
          isDelayed={isDelayed}
          delayedTime={delayedTime}
          isInBucket={isInBucket}
          isNHC={isNHC}
          onEmailDrawerOpen={onEmailDrawerOpen}
          onEmailPreviewModalOpen={handleEmailPreviewModalOpen}
          openDetails={openHomeDetails}
          openHistoryAttempt={(attempt) =>
            handleOpenCloseoutDrawer(attempt, { tab: CLOSEOUT_TAB.HISTORY })
          }
          startAutoCloseTimeout={startAutoCloseTimeout}
          stopAutoCloseTimeout={stopAutoCloseTimeout}
        />
      </Box>
      <TaskCardMenu
        closeContextMenu={closeContextMenu}
        handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
        onEmailDrawerOpen={onEmailDrawerOpen}
        onEmailModalOpen={onEmailModalOpen}
        contextMenu={contextMenu}
        setModalType={setModalType}
        onAttemptUpdated={onAttemptUpdated}
        modalType={modalType}
        attempt={attempt}
        isInBucket={isInBucket}
        updateCalendarCounts={updateCalendarCounts}
        calendarSet={calendarSet}
        setHasModalOpen={setHasModalOpen}
      />
      {emailPreviewDetails && (
        <EmailActivityPreviewModal
          isEmailModalOpen
          activity={emailPreviewDetails}
          details={emailPreviewDetails}
          closeEmailModal={handleEmailPreviewModalClose}
        />
      )}
    </>
  );
}

const useStyles = makeStyles()(
  (
    theme,
    {
      submittedStatus,
      communityColor,
      assignmentTemplateColor,
      isSelected,
      isOnInactiveInterval,
      isMinimized,
      isUnscheduled,
      derivedStatus,
      hideFooter,
      readOnly,
      isMultiTechnician,
      isAttemptMisaligned,
      loading,
      isInSearch,
      capacityColor,
      isRequestedInDateRange,
      isInBucket,
    }
  ) => {
    let cursor = "grab";

    const colors = ATTEMPT_DERIVED_STATUS_COLORS_MAP[derivedStatus] || {};
    const textColor = theme.palette.common.black;
    const selected = {
      borderWidth: 2,
    };

    const hover = {
      borderColor: theme.palette.blue.accent,
    };

    let borderStyle = "solid";
    let borderWidth = 1;
    let borderColor =
      colors.cardBorder || colors.main || theme.palette.grey.darkerAccent;

    if (isMultiTechnician) {
      borderColor = theme.palette.warning.main;
      borderWidth = 2;
    }

    if (isAttemptMisaligned) {
      borderColor = theme.palette.error.main;
      borderStyle = "dashed";
    }

    if (readOnly) {
      cursor = "pointer";
    } else if (isSelected) {
      cursor = "grabbing";
    }

    return {
      card: {
        boxSizing: "border-box",
        position: "relative",
        borderRadius: theme.spacing(1),
        height: "inherit",
        width: COLUMN_WIDTH - 6,
        cursor,
        ...(!isRequestedInDateRange &&
          isInBucket && {
            backgroundImage: `repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.12) 0px, rgba(0, 0, 0, 0.12) 2px, transparent 2px, transparent 6px)`,
          }),
        backgroundColor:
          colors.submittedStatusCardBackground?.[submittedStatus] ||
          colors.cardBackground ||
          theme.palette.common.white,
        "&:hover": isSelected ? selected : hover,
        "&:active": { opacity: 0.5 },
        overflow: "clip",
        zIndex: isSelected ? 5 : 0,
        color: theme.palette.grey.main,
        display: "flex",
        borderWidth,
        borderColor,
        borderStyle,
        flexDirection: "column",
        containerType: "size",
        opacity: loading ? 0.5 : 1,
        ...((isMinimized || isUnscheduled) && {
          border: `1px solid ${theme.palette.orange.medium}`,
          boxShadow: `0px 0px 3px 1px ${theme.palette.orange.mediumLight}`,
        }),
        ...(isOnInactiveInterval && {
          border: `1px dashed ${theme.palette.red.medium}`,
          boxShadow: `0px 0px 3px 1px ${theme.palette.red.accent}`,
        }),
        ...(isInSearch && {
          border: `2px solid ${theme.palette.blue.accent}`,
          boxShadow: `0px 0px 3px 1px ${theme.palette.blue.accent}`,
        }),
      },
      header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: communityColor,
        height: SECTION_HEIGHT,
        position: "relative",
        zIndex: 3,
        ...(!isRequestedInDateRange &&
          isInBucket && {
            backgroundImage: `repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.12) 0px, rgba(0, 0, 0, 0.12) 2px, transparent 2px, transparent 6px)`,
          }),
      },
      attemptType: {
        backgroundColor: assignmentTemplateColor,
        color: getContrastColor(assignmentTemplateColor),
        borderRadius: theme.spacing(0.5),
        fontSize: theme.fontSize.xsmall,
        height: SECTION_HEIGHT,
        width: 28,
      },
      community: {
        fontSize: theme.fontSize.small,
        color: getContrastColor(communityColor),
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: theme.spacing(20),
      },
      headerContainer: {
        display: "flex",
        gap: 4,
        alignItems: "center",
      },
      headerRightContainer: {
        display: "flex",
        gap: 0.5,
        alignItems: "center",
      },
      builder: {
        fontSize: theme.fontSize.small,
        fontWeight: theme.fontWeight.bold,
        paddingRight: theme.spacing(0.5),
      },
      addressSection: {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        position: "relative",
        bottom: theme.spacing(0.5),
        padding: theme.spacing(0, 0.5),
        alignItems: "flex-start",
        height: SECTION_HEIGHT,
        color: textColor,
        zIndex: 1,
      },
      issuesTitle: {
        fontSize: theme.fontSize.small,
        color: "inherit",
      },
      requestedStartDateSection: {
        width: "100%",
        height: SECTION_HEIGHT,
        padding: theme.spacing(0, 0.5, 0.5, 0.5),
        display: "flex",
        alignItems: "center",
      },
      requestedStartDate: {
        fontSize: theme.fontSize.small,
        color: textColor,
      },
      footer: {
        height: SECTION_HEIGHT,
        backgroundColor: isInBucket ? "transparent" : "inherit",
        padding: theme.spacing(0, 0.5),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "absolute",
        color: textColor,
        bottom: 0,
        width: "100%",
        zIndex: hideFooter ? 0 : 2,
      },
      icons: {
        display: "flex",
        gap: theme.spacing(0.25),
        alignItems: "center",
        paddingBottom: theme.spacing(0.25),
      },
      address: {
        fontSize: theme.fontSize.small,
        overflow: "clip",
        color: "inherit",
        width: 88,
      },
      city: {
        fontSize: theme.fontSize.small,
        textAlign: "right",
        whiteSpace: "nowrap",
        overflow: "clip",
        fontStyle: "italic",
        color: theme.palette.grey.medium,
      },
      capacityCard: {
        boxSizing: "border-box",
        position: "relative",
        borderRadius: theme.spacing(0.5),
        height: "inherit",
        width: COLUMN_WIDTH,
        backgroundColor: `color-mix(in oklch, ${capacityColor} 10%, #f5f5f5)`,
        border: `2px solid color-mix(in srgb, ${capacityColor} 50%, #f5f5f5)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      capacityText: {
        fontSize: theme.fontSize.small,
        fontWeight: theme.fontWeight.semiBold,
        color: `color-mix(in srgb, ${capacityColor} 80%, #f5f5f5)`,
      },
      invoicedText: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
        textTransform: "uppercase",
        color: theme.palette.grey.darkerAccent,
        marginTop: theme.spacing(1),
      },
    };
  }
);
